import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  forwardRef,
  ChangeDetectorRef,
  Inject,
  ChangeDetectionStrategy,
} from "@angular/core";

import { ICGoodFromUntilModel } from "../models/bfx-good-from-until.model";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ICNInputControlState } from "../models/bfx-interface-controls.enums";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  MarketTimeConstantImpl,
  moment,
  MarketTimeConstantType,
} from "shared/cubiq-model-multi-asset";
import {
  BfxAutoCompleteComponent,
  BfxAutoCompleteComponentState,
} from "../../../../bfx-element-library/bfx-autocomplete.component";
import {
  ICAutocompleteGenericModel,
  ICTimeControlModel,
} from "../models/bfx-interface-controls.models";

export const CURRENT_DATE = CubiqTS.moment().subtract(1, "days");
export const NOSECS_TIME_FORMAT: string = "HH:mm";
export const SECS_TIME_FORMAT: string = "HH:mm:ss";

export const GOOD_FROM_UNTIL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxGoodFromUntilComponent),
  multi: true,
};
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OENActivationExpiryMode } from "app/order-entry/data/enums/oe-activation-expiry-mode.enum";
import { OETimeMachineService } from "app/order-entry/services/basic/oe-time-machine.service";
import { BfxCalendarComponent } from "./bfx-calendar.component";
import { BfxTimeInputComponent, INPUT_TIME_SHOWSECONDS } from "../bfx-time-input.component";
import { ActiveUserProfilePromiseService } from "../../../../../shared/auth-services";
import { BfxInputControlStateEnum } from "../../../../../shared/base-components/models/base-components.enums";
import { setThrowInvalidWriteToSignalError } from "@angular/core/primitives/signals";

@Component({
  selector: "bfx-good-from-until",
  templateUrl: "./html/bfx-good-from-until.html",
  providers: [GOOD_FROM_UNTIL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxGoodFromUntilComponent implements OnInit, ControlValueAccessor {
  public config: any = {
    minutesBeforeAllowed: 0,
    updateToCurrentOnValidate: false,
    goodFromUntilAutocompleteConfig: {
      allowEmpty: true,
      forceSelection: true,
      customValidation: null,
    },
  };

  public data = {
    value: null,
    calendarValue: null,
    labelValue: null,
    dateValue: null,
    timeValue: null,
    marketValue: null,
    timezoneValue: null,
    defaultTimezoneValue: null,
    marketList: [],
    marketNames: [],
    marketConstantsLoaded: false,
    marketSuggestions: [],
    locked: false,
    calendarLocked: false,
    calendarVisible: true,
    inputTimeFieldLocked: false,
    inputTimeFieldVisible: true,
    autoCompleteInputPosition: null,
    state: ICNInputControlState.INACTIVE,
    errorMsg: null,
    showError: false,
    input: {
      value: null,
      label: "Good From/Until",
      placeholder: null,
      locked: false,
      state: ICNInputControlState.INACTIVE,
    },
  };

  private msgTemplate: Map<string, string> = new Map([
    ["format", "Invalid entry. Good From/Until must be a valid market time"],
    ["zone", "Invalid Time Zone!"],
    ["future", "Value must be in the future."],
    ["constraint_from", "Good From must be before a valid Good Until."],
    ["constraint_until", "Good Until must be after a valid Good From."],
    ["timeout_parent_required", "Parent in an On Timeout strategy cannot be GTC."],
    [
      "timeout_before_parent",
      "Child order in an On Timeout strategy cannot have an expiry time earlier than its parent.",
    ],
    [
      "if-done_before_parent",
      "Child order in an If Done strategy cannot have an expiry time earlier than its parent.",
    ],
    ["must_be_gtc_for_mkt", "Value must be GTC for MARKET order."],
  ]);

  panelStyleClass: string = "";

  public inputFocused: boolean = false;

  @Input("placeholder") input_placeholder: string;

  @Input("label") input_label: string;

  @Input("panelStyleClass") panel_class: string;

  @Input("mode") mode: OENActivationExpiryMode; // from - false, until - true

  pendingValueModel: { valueModel: ICGoodFromUntilModel; isAlgo: boolean };

  defaultDayConstant: CubiqTS.Moment = null;

  private _inputTimeField: BfxTimeInputComponent;

  getDefaultCustomFromUntilModel(): ICGoodFromUntilModel {
    const now = CubiqTS.moment();
    const model = new ICGoodFromUntilModel();
    model.dateValue = now.format("DD-MMM-YYYY");
    model.timeValue = now.format("HH:mm");
    model.timezoneValue = this.data.defaultTimezoneValue;
    model.marketValue = "CUSTOM";
    model.dayOrder = false;
    model.isNow = false;
    model.isCustom = true;
    return model;
  }

  getNowGoodFromUntilModel(): ICGoodFromUntilModel {
    const model = new ICGoodFromUntilModel();
    model.marketValue = "NOW";
    model.dateValue = null;
    model.isNow = true;
    model.dayOrder = false;
    return model;
  }

  getGTCGoodFromUntilModel() {
    const model = new ICGoodFromUntilModel();
    model.marketValue = this.data.input.value;
    model.dayOrder = false;
    model.dateValue = null;
    return model;
  }

  public get inputTimeField(): BfxTimeInputComponent {
    return this._inputTimeField;
  }

  @ViewChild("inputTimeField")
  public set inputTimeField(value: BfxTimeInputComponent) {
    const loadPendingState = this._inputTimeField == null && this.pendingState != null;
    const loadPendingValueModel = this._inputTimeField == null && this.pendingValueModel != null;

    this._inputTimeField = value;

    if (loadPendingState) this.setValidityState(this.pendingState.state, this.pendingState.msg);

    if (loadPendingValueModel) {
      this.loadGoodFromUntil(this.pendingValueModel.valueModel, this.pendingValueModel.isAlgo);
    }
  }

  private static calendarNameIndex = 0;

  public calendarName: string = "goodFromUntil" + ++BfxGoodFromUntilComponent.calendarNameIndex;

  private _input_constraint: ICGoodFromUntilModel;

  private pendingState: { state: BfxInputControlStateEnum; msg: string };

  public get input_constraint(): ICGoodFromUntilModel {
    return this._input_constraint;
  }

  public get input_constraint_as_moment(): CubiqTS.Moment {
    let moment = this._oeTimeMachine.createMoment4Model(this.input_constraint);
    if (moment != null && moment instanceof CubiqTS.moment && moment.isValid()) {
      return moment;
    }
    return null;
  }

  @Input("constraint")
  public set input_constraint(value: ICGoodFromUntilModel) {
    this._input_constraint = value;
    setTimeout(() => {
      this.validateConstraint(this._oeTimeMachine.createMoment4Model(this.data.value));
    }, 0);
  }

  public get input_constraintValid(): boolean {
    // null implies NOW/GTC/DAY so don't fail validation
    return this._input_constraint == null || this._input_constraint?.valid === true;
  }

  input_externalState: ICNInputControlState;

  get externalState() {
    return this.input_externalState;
  }

  @Input()
  set externalState(value: any) {
    this.input_externalState = value;
    this.setValidityState(value, this.errorMsg);
  }

  input_errorMsg: string;

  get errorMsg() {
    return this.input_errorMsg;
  }

  @Input()
  set errorMsg(value: any) {
    this.input_errorMsg = value;
    let error = this.msgTemplate.get(this.errorMsg);
    if (error != null && !this.data.showError) {
      // if original error is active show that one not the new one
      this.data.errorMsg = error;
      this.data.showError = true;
    }
  }

  @Input()
  tooltipLabel: string;

  @Input()
  isAlgo: boolean = false;

  @Output("onBlur")
  public blur: EventEmitter<any> = new EventEmitter<any>();

  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  private _autocompleteControl: BfxAutoCompleteComponent;

  public get autocompleteControl(): BfxAutoCompleteComponent {
    return this._autocompleteControl;
  }

  @ViewChild("autocompleteControl", { static: false })
  public set autocompleteControl(value: BfxAutoCompleteComponent) {
    const loadPendingState = this._autocompleteControl == null && this.pendingState != null;
    const loadPendingValueModel =
      this._autocompleteControl == null && this.pendingValueModel != null;

    this._autocompleteControl = value;

    if (loadPendingState) this.setValidityState(this.pendingState.state, this.pendingState.msg);

    if (loadPendingValueModel)
      this.loadGoodFromUntil(this.pendingValueModel.valueModel, this.pendingValueModel.isAlgo);
  }

  private _calendarControl: BfxCalendarComponent;

  public get calendarControl(): BfxCalendarComponent {
    return this._calendarControl;
  }

  @ViewChild("calendarControl")
  public set calendarControl(value: BfxCalendarComponent) {
    const loadPendingState = this._calendarControl == null && this.pendingState != null;
    const loadPendingValueModel = this._calendarControl == null && this.pendingValueModel != null;

    this._calendarControl = value;

    if (loadPendingState) this.setValidityState(this.pendingState.state, this.pendingState.msg);

    if (loadPendingValueModel)
      this.loadGoodFromUntil(this.pendingValueModel.valueModel, this.pendingValueModel.isAlgo);
  }

  onModelChange: Function = (value: any) => {};

  onModelTouched: Function = () => {};

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    private _activeUserLookup: ActiveUserProfilePromiseService,
    private _oeTimeMachine: OETimeMachineService,
    public cd: ChangeDetectorRef,
    @Inject(INPUT_TIME_SHOWSECONDS) public showSeconds: boolean,
  ) {}

  ngOnInit() {
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    // set placeholder value
    if (
      this.input_placeholder != null &&
      typeof this.input_placeholder === "string" &&
      this.input_placeholder.length > 0
    ) {
      this.data.input.placeholder = this.input_placeholder;
    }

    // set label value
    if (
      this.input_label != null &&
      typeof this.input_label === "string" &&
      this.input_label.length > 0
    ) {
      this.data.input.label = this.input_label;
    }

    // set label value
    if (this.panel_class != null && this.panel_class.length > 0) {
      this.panelStyleClass = this.panel_class;
    }

    //this.data.timezoneDataset = new Array<BfxAutocompleteGenericModel>();
    // set autocomplete input properties
    this.refreshGoodUntilConstants(null, null, null, this.data.input.placeholder, this.isAlgo);
    this._activeUserLookup.getActiveUserData().then((user) => {
      this.data.defaultTimezoneValue = user.timeZoneCd;
    });
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value = value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onInputBlur(event: any): void {
    this.inputFocused = false;
    this.data.state = ICNInputControlState.INACTIVE;
    this.blur.emit(event);
    this.validate();
  }

  public onInputFocus(event: any): void {
    this.inputFocused = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.onModelTouched();
    this.focus.emit(event);
  }

  public onInputClear(): void {
    this.resetInputControl();
  }

  public onChange(event: any): void {
    this.cd.markForCheck();

    this.onModelChange(this.data.value);
  }

  onFieldIconClick(
    $event: { event: Event; type: "start" | "end"; index: number },
    autocomplete: BfxAutoCompleteComponent,
    calendarControl: BfxCalendarComponent,
  ) {
    if ($event.type === "end" && $event.index === 0) {
      autocomplete.handleDropdownClick($event);
    }
  }

  private updateModelValueDayOrder(model: ICGoodFromUntilModel): ICGoodFromUntilModel {
    if (!model.dayOrder) {
      return model;
    }

    let found: CubiqTS.MarketTimeConstantImpl = this.getMarketTimeConstantImpl("DAY");

    model.dateValue =
      found.constantDate != null ? CubiqTS.moment(found.constantDate).format("DD-MMM-YYYY") : null;
    model.timeValue =
      found.constantTime != null
        ? CubiqTS.moment(found.constantTime.toString().padStart(4, "0"), "HHmm").format("HH:mm")
        : null;
    model.timezoneValue = found.timeZoneCd;

    return model;
  }

  public loadGoodFromUntil(valueModel: ICGoodFromUntilModel, isAlgo: boolean = false): boolean {
    let result = true;
    if (
      this.calendarControl == null ||
      this._inputTimeField == null ||
      this._autocompleteControl == null
    ) {
      this.pendingValueModel = { valueModel: valueModel, isAlgo: isAlgo };
      return;
    } else {
      this.pendingValueModel = null;
      let inputTimeControl = this.inputTimeField.data;

      let formattedTime: string = "";

      if (valueModel?.dayOrder) {
        valueModel = this.updateModelValueDayOrder(valueModel);

        if (valueModel.dateValue == null) {
          valueModel = this._oeTimeMachine.createDefaultUnpopulatedModelForUserTz();
        }
      }

      if (valueModel?.timeValue != null && valueModel.timeValue instanceof ICTimeControlModel) {
        formattedTime = valueModel.timeValue.timeValue;
      } else if (typeof valueModel?.timeValue === "string") {
        formattedTime = valueModel.timeValue;
      } else {
        formattedTime = null;
      }

      // populate value
      if (valueModel != null) {
        const isNowOrGTC = valueModel?.isNow || valueModel?.marketValue === "GTC";
        valueModel.isLoadPending = false;
        this.data.value = valueModel;
        this.data.calendarValue =
          valueModel.dateValue != null
            ? CubiqTS.moment(valueModel.dateValue, "DD-MMM-YYYY").toDate()
            : null;
        this.data.dateValue = valueModel.dateValue;
        this.data.marketValue = valueModel.marketValue;
        this.data.timeValue = formattedTime;
        this.data.timezoneValue = valueModel.timezoneValue;
        if (this.data.marketValue === "CUSTOM") {
          this.data.input.value = "CUSTOM";
          this.data.labelValue = null;
        } else if (this.data.marketValue === "DAY") {
          this.data.input.value = "DAY";
          this.data.labelValue = valueModel.dateValue;
        } else if (valueModel?.isNow) {
          this.data.input.value = "NOW";
          this.loadNowOrGTC();
        } else if (valueModel?.marketValue === "GTC") {
          this.data.input.value = "GTC";
          this.loadNowOrGTC();
        } else {
          this.data.input.value = valueModel.marketValue;
          this.data.labelValue = "DD-MMM-YYYY";
        }

        this.calendarControl.data.dateValue = valueModel.dateValue;
        this.calendarControl.writeValue(this.data.calendarValue);
        this.calendarControl.disabled = isNowOrGTC;
        this.inputTimeField.disabled = isNowOrGTC;
        this.data.inputTimeFieldLocked = isNowOrGTC;
        this.data.calendarVisible = !isNowOrGTC;
        this.data.inputTimeFieldVisible = !isNowOrGTC;
        this.calendarControl.updateFilledState();
        this.data.calendarLocked = isNowOrGTC;
        inputTimeControl.timeValue = formattedTime;
        if (valueModel.isNow || valueModel.marketValue == "GTC") {
          inputTimeControl.timezoneValue = this.getDefaultTimezoneObject();
        } else if (this.data.timezoneValue != null) {
          inputTimeControl.timezoneValue = inputTimeControl.timezoneDataset.find(
            (data) => data?.value === this.data.timezoneValue,
          );
        } else {
          inputTimeControl.timezoneValue = this.getDefaultTimezoneObject();
        }
      } else {
        this.data.value = null;
        this.data.dateValue = null;
        this.data.marketValue = null;
        this.data.timeValue = null;
        this.data.timezoneValue = this.getDefaultTimezone();
        inputTimeControl.timezoneValue = this.getDefaultTimezoneObject();
        this.data.input.value = null;
        this.data.labelValue = null;
        this.calendarControl.writeValue(null);
        inputTimeControl.timeValue = null;
      }
      result = this.validate();
    }
    this.onChange(this.data.value);
    this.cd.markForCheck();

    return result;
  }

  private loadNowOrGTC() {
    this.data.labelValue = null;
    this.data.value.valid = true;
    this.data.dateValue = this.data.value.dateValue;
    this.data.calendarValue = null;
    this.calendarControl.data.dateValue = this.data.value.dateValue;
    this.calendarControl.writeValue(this.data.calendarValue);
    this.data.calendarLocked = true;
    this.data.calendarVisible = false;
    this.data.inputTimeFieldLocked = true;
    this.data.inputTimeFieldVisible = false;
    this.calendarControl.disabled = true;
    this.inputTimeField.disabled = true;
    this.inputTimeField.data.timeValue = null;
    this.inputTimeField.data.value = null;
    this.inputTimeField.data.timeInputModel = null;
    this.inputTimeField.data.timezoneValue = this.getDefaultTimezoneObject();
  }

  private getDefaultTimezone() {
    return this.data.defaultTimezoneValue;
  }

  private getDefaultTimezoneObject() {
    return {
      label: this.data.defaultTimezoneValue,
      value: this.data.defaultTimezoneValue,
    };
  }

  public resetInputControl(): void {
    this.externalState = null;
    this.errorMsg = null;
    this.data.state = ICNInputControlState.INACTIVE;
    this.data.errorMsg = null;
    this.data.showError = false;

    this.inputFocused = false;

    setTimeout(() => {
      const options = this.autocompleteControl?.suggestions;
      let value;
      if (this.mode == OENActivationExpiryMode.EXPIRY && this.isAlgo) {
        const model = this._oeTimeMachine.createDefaultUnpopulatedModelForUserTz();
        this.defaultDayConstant = model.defaultDayConstant;
        value = "DAY";
      } else if (this.mode == OENActivationExpiryMode.EXPIRY) {
        value = "GTC";
      } else {
        value = "NOW";
      }

      const option = options?.find((o) => o === value);

      if (option != null) {
        this.autocompleteControl.selectItem(option, false);
      }
    }, 4);
  }

  public resetAutocomplete(): void {
    this.data.input.value = null;
    this.data.input.placeholder = this.input_placeholder;
    this.data.input.locked = false;
    this.externalState = null;
    this.data.errorMsg = null;
    this.data.showError = false;
    this.data.input.state = ICNInputControlState.INACTIVE;
    this.autocompleteControl.state = BfxAutoCompleteComponentState.NONE;
  }

  public resetCalendar(): void {
    let calData = this.calendarControl.data;
    calData.dateValue = null;
    calData.timeValue = null;
    calData.timezoneValue = this.getDefaultTimezone();
    this.inputTimeField.resetInputControl();
    this.calendarControl.value = null;
    this.calendarControl.data.dateValue = null;
    this.calendarControl.data.timeValue = null;
    this.calendarControl.data.timeInputModel = null;
    this.calendarControl.data.timezoneValue = null;
    this.calendarControl.data.marketValue = null;
  }

  public lockInputControl(): void {
    this.data.locked = true;

    if (this.calendarControl != null) {
      this.calendarControl.disabled = true;
    }

    if (this.inputTimeField != null) {
      this.inputTimeField.disabled = true;
    }

    // Don't want any invalid-related styles bleeding trough - we reset validity and recheck on
    // unlocking
    this.setValidityState(ICNInputControlState.INACTIVE);
    this.cd.markForCheck();
  }

  public unlockInputControl(): void {
    this.data.locked = false;

    if (this.calendarControl != null) {
      this.calendarControl.disabled = this.data.calendarLocked;
    }

    if (this.inputTimeField != null) {
      this.inputTimeField.disabled = this.data.inputTimeFieldLocked;
    }
    // Revalidate as invalid styles should now be displayed
    this.validate();

    this.cd.markForCheck();
  }

  marketSuggestion(event): void {
    let includeGTCandDAY = false;
    if (this.mode == OENActivationExpiryMode.EXPIRY) {
      includeGTCandDAY = true;
    }
    let filtered = this.searchMarketTimeConstantNames(event.query, includeGTCandDAY);
    this.data.marketSuggestions = filtered ?? [];
    this.cd.markForCheck();
  }

  hasMarketTime(): boolean {
    return this.data.timeValue != null && this.data.dateValue != null;
  }

  selectedMarketLabelHandler(selectedMarket: string, isRefresh: boolean = false): boolean {
    this.cd.markForCheck();

    if (selectedMarket !== "DAY") {
      this.defaultDayConstant = null;
    }

    this.calendarControl?.hideOverlay();
    this.inputFocused = false;

    const hasCustom = selectedMarket === "CUSTOM";
    const hasNow =
      selectedMarket === "NOW" || (selectedMarket == null && this.data.input.placeholder === "NOW");
    const hasGTC =
      selectedMarket === "GTC" || (selectedMarket == null && this.data.input.placeholder === "GTC");

    if (hasCustom) {
      if (this.data.dateValue == null) {
        let model = this.getDefaultCustomFromUntilModel();
        return this.loadGoodFromUntil(model, this.isAlgo);
      }
      return this.validate();
    }

    if (hasNow) {
      let model = this.getNowGoodFromUntilModel();
      return this.loadGoodFromUntil(model, this.isAlgo);
    }

    if (hasGTC) {
      let model = this.getGTCGoodFromUntilModel();
      return this.loadGoodFromUntil(model, this.isAlgo);
    }

    const selectedMarketTime = this.searchMarketTimeConstant(selectedMarket, isRefresh);
    const selectedMarketDate = this.searchMarketDateConstant(selectedMarket);

    let model = new ICGoodFromUntilModel();
    model = this.getMarketTimeAndDateModel(selectedMarketTime, selectedMarketDate, model);

    if (model != null) {
      this.data.calendarLocked = false;
      this.data.inputTimeFieldLocked = false;
      return this.loadGoodFromUntil(model, this.isAlgo);
    }

    if (
      selectedMarket == "DAY" ||
      (selectedMarket == null && this.data.input.placeholder == "DAY")
    ) {
      const model = this._oeTimeMachine.createDefaultUnpopulatedModelForUserTz();
      this.defaultDayConstant = model.defaultDayConstant;
      return this.loadGoodFromUntil(model, this.isAlgo);
    }

    logger.out.error(`selectedMarketLabelHandler(${selectedMarket}) not matched.`);
  }

  private getMarketTimeAndDateModel(
    selectedMarketTime: CubiqTS.MarketTimeConstantImpl,
    selectedMarketDate: CubiqTS.MarketTimeConstantImpl,
    model: ICGoodFromUntilModel,
  ): ICGoodFromUntilModel {
    if (selectedMarketTime && selectedMarketDate) {
      model = this.populateSelectedMarketTimeHandler(selectedMarketTime, model);

      if (model.timeValue != null && model.timezoneValue) {
        model = this.populateSelectedMarketDateHandler(selectedMarketDate, model);

        if (model.dateValue != null) return model;
      }
    }
    return null;
  }

  private searchMarketTimeConstant(selectedMarket: string, isRefresh: boolean = false) {
    let item: MarketTimeConstantImpl = this.data.marketList.find(
      (name) => name.constantName.toLowerCase() === selectedMarket?.toLowerCase(),
    );

    if (
      this.data.input.value == "DAY" &&
      this.data.input.placeholder === "GTC" &&
      selectedMarket === "DAY" &&
      this.data.marketConstantsLoaded == false &&
      !isRefresh
    ) {
      this.refreshGoodUntilConstants(null, null, null, "DAY", this.isAlgo, selectedMarket);
    }
    return item;
  }

  private populateSelectedMarketTimeHandler(
    item: MarketTimeConstantImpl,
    model: ICGoodFromUntilModel,
  ) {
    if (CubiqTS.Long.isLong(item.constantTime)) {
      let strTime = item.constantTime.greaterThan(0) ? item.constantTime.toString() : "0000";
      model.timeValue = moment(strTime, "Hmm").format(
        this.showSeconds ? SECS_TIME_FORMAT : NOSECS_TIME_FORMAT,
      );
    }
    model.timezoneValue = item.timeZoneCd;
    model.marketValue = item.constantName;
    model.dayOrder = item?.constantName === "DAY";

    // set system configured date
    if (item.constantDate != null && CubiqTS.Long.isLong(item.constantTime)) {
      model.dateValue = moment(item.constantDate).utc().format("DD-MMM-YYYY");
    }

    // check if type is DATE or TODAY and set today's date
    if (
      (item.constantDate == null && item.constantType == MarketTimeConstantType.DATE) ||
      item.constantType == MarketTimeConstantType.TODAY
    ) {
      const today = moment();
      model.dateValue = today.format("DD-MMM-YYYY");
    }

    // check if type is TOMORROW and set tomorrow's date
    if (item.constantDate == null && item.constantType == MarketTimeConstantType.TOMORROW) {
      const tomorrow = moment().add(1, "d");
      model.dateValue = tomorrow.format("DD-MMM-YYYY");
    }

    return model;
  }

  searchMarketDateConstant(marketLabel: any): CubiqTS.MarketTimeConstantImpl {
    return this.data.marketList.find(
      (item) => item.constantName.toLowerCase() === marketLabel?.toLowerCase(),
    );
  }

  populateSelectedMarketDateHandler(marketDateConstant, model): ICGoodFromUntilModel {
    if (marketDateConstant == null) {
      return null;
    }

    if (
      (marketDateConstant.constantDate == null &&
        marketDateConstant.constantType === MarketTimeConstantType.DATE) ||
      marketDateConstant.constantType === MarketTimeConstantType.TODAY
    ) {
      model.dateValue = moment().format("DD-MMM-YYYY");
    } else if (
      marketDateConstant.constantDate == null &&
      marketDateConstant.constantType == MarketTimeConstantType.TOMORROW
    ) {
      model.dateValue = moment().add(1, "d").format("DD-MMM-YYYY");
    } else if (model.dateValue == null) {
      // not a MarketTimeConstantType constant  or set in populateSelectedMarketTimeHandler
      return null;
    }

    return model;
  }

  public refreshGoodUntilConstants(
    calendar,
    base,
    nonBase,
    defaultMarketTime: string = null,
    isAlgo: boolean = false,
    selectedMarket: string = null,
  ): void {
    let marketTimeConstants: {
      names: Array<string>;
      constants: Array<CubiqTS.MarketTimeConstantImpl>;
    } = this._oeTimeMachine.loadMarketTimeConstants(
      calendar,
      base,
      nonBase,
      this.mode === OENActivationExpiryMode.EXPIRY,
    );
    let wasDayLoaded: boolean = this.isDayConstantLoaded();

    this.data.marketList = marketTimeConstants.constants;
    this.data.marketNames = marketTimeConstants.names;

    this.marketSuggestion({ query: null });
    // Loaded only when we have the pair
    this.data.marketConstantsLoaded = base != null && nonBase != null;

    let model: ICGoodFromUntilModel = this.data.value;

    // if there is a default and nothing is selected - then select the default
    // if it's DAY - do it anyway as we need to reload the details
    if (null == this.data.marketValue) {
      if (null != defaultMarketTime && defaultMarketTime == "DAY") {
        let found: CubiqTS.MarketTimeConstantImpl =
          this.getMarketTimeConstantImpl(defaultMarketTime);
        if (found?.constantDate != null) {
          model = new ICGoodFromUntilModel();
          model.marketValue = found.constantName;
          model.dayOrder = defaultMarketTime == "DAY";
          model.dateValue =
            found.constantDate != null
              ? CubiqTS.moment(found.constantDate).format("DD-MMM-YYYY")
              : null;
          model.timeValue =
            found.constantTime != null
              ? CubiqTS.moment(found.constantTime.toString().padStart(4, "0"), "HHmm").format(
                  "HHmm",
                )
              : null;
          model.timezoneValue = found.timeZoneCd;

          // NB - if the value is the same as the placeholder - need ot make it inactive
          this.setValidityState(ICNInputControlState.INACTIVE);
          this.defaultDayConstant = null;
        } else {
          model = this._oeTimeMachine.createDefaultUnpopulatedModelForUserTz();
          this.defaultDayConstant = model.defaultDayConstant;
          this.setValidityState(ICNInputControlState.VALID);
        }
      }
    }

    // We have selected DAY before it was loaded, need to update the model now
    if (
      (this.data.marketValue === "DAY" && !wasDayLoaded && this.isDayConstantLoaded()) ||
      selectedMarket === "DAY"
    ) {
      let found: CubiqTS.MarketTimeConstantImpl = this.getMarketTimeConstantImpl(
        this.data.marketValue,
      );
      if (found?.constantDate != null) {
        model = new ICGoodFromUntilModel();
        model.marketValue = found.constantName;
        model.dayOrder = defaultMarketTime == "DAY";
        model.dateValue =
          found.constantDate != null
            ? CubiqTS.moment(found.constantDate).format("DD-MMM-YYYY")
            : null;
        model.timeValue =
          found.constantTime != null
            ? CubiqTS.moment(found.constantTime.toString().padStart(4, "0"), "HHmm").format("HHmm")
            : null;
        model.timezoneValue = found.timeZoneCd;

        // NB - if the value is the same as the placeholder - need ot make it inactive
        this.defaultDayConstant = null;
        this.setValidityState(ICNInputControlState.INACTIVE);
      } else {
        model = this._oeTimeMachine.createDefaultUnpopulatedModelForUserTz();
        this.defaultDayConstant = model.defaultDayConstant;
        this.setValidityState(ICNInputControlState.VALID);
      }
    }
    this.loadGoodFromUntil(model, this.isAlgo);

    // select the chosen market time - so it will update the label showing its time.
    if (this.data.marketValue !== "CUSTOM" && this.data.marketValue != null) {
      this.selectedMarketLabelHandler(this.data.marketValue, true);
    } else {
      this.validate();
    }
    this.cd.markForCheck();
  }

  /**
   * do not set anything other than setValidityState(state: ICNInputControlState)
   * this is for validation only. -dc
   */
  public validate(): boolean {
    if (this.data.locked) {
      // We never want to show goodFrom/until as invalid if it's locked e.g. when we're waiting for Acceptance
      return true;
    }
    let validityOutcome = false;
    this.data.calendarVisible = this.data.inputTimeFieldVisible =
      this.data.input.value != "GTC" && this.data.input.value != "NOW";

    if (this.inputFocused) {
      this.setValidityState(ICNInputControlState.ACTIVE);
      validityOutcome = true;
      this.cd.markForCheck();
    } else if (
      this.data.input.value == "DAY" &&
      !this.isDayConstantLoaded() &&
      this.defaultDayConstant == null
    ) {
      this.setValidityState(ICNInputControlState.VALID);
      validityOutcome = true;
    } else if (
      this.data.value?.isNow === true ||
      (this.data.input.value != null &&
        (this.data.input.value == "GTC" ||
          (this.data.input.value == null && this.data.input.placeholder == "GTC") ||
          this.data.input.value == "NOW" ||
          (this.data.input.value == "DAY" && !this.isDayConstantLoaded())))
    ) {
      this.setValidityState(ICNInputControlState.INACTIVE);
      validityOutcome = true;

      if (
        this.data.value?.isNow === true ||
        (this.data.input.value != null &&
          (this.data.input.value == "GTC" ||
            (this.data.input.value == null && this.data.input.placeholder == "GTC") ||
            this.data.input.value == "NOW"))
      ) {
        this.data.calendarLocked = this.data.inputTimeFieldLocked = true;
        this.data.calendarVisible = this.data.inputTimeFieldVisible = false;
      }
      this.cd.markForCheck();
    } else {
      if (this.data.input.value != null && this.data.input.value !== "") {
        let inputValid: boolean =
          this.data.dateValue != null &&
          this.data.dateValue !== "" &&
          this.data.timeValue != null &&
          this.data.timeValue !== "" &&
          this.data.timezoneValue != null &&
          this.data.timezoneValue !== "" &&
          this.data.marketValue != null &&
          this.data.marketValue !== "";

        let mktC: CubiqTS.MarketTimeConstantImpl = this.getMarketTimeConstantImpl(
          this.data.input.value,
        );

        if (null != mktC) {
          let nowMoment = CubiqTS.moment();
          let controlMoment = this.createMoment4MarketConstant(this.data.input.value);
          if (
            nowMoment != null &&
            CubiqTS.moment.isMoment(nowMoment) &&
            nowMoment.isValid() &&
            controlMoment != null &&
            CubiqTS.moment.isMoment(controlMoment) &&
            controlMoment.isValid()
          ) {
            if (nowMoment.isBefore(controlMoment)) {
              validityOutcome = this.validateConstraint(controlMoment, true);
            } else {
              this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("future"));
            }
          } else {
            this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("format"));
          }
        } else if (this.isValidCustom(inputValid)) {
          let timeValue =
            this.data.timeValue instanceof ICTimeControlModel
              ? this.data.timeValue.timeValue
              : this.data.timeValue;
          let dateTime = this.data.dateValue + " " + timeValue;
          validityOutcome = this.validateMoment(dateTime, this.data.timezoneValue);
        } else if (
          this.data.input.value === "DAY" &&
          this.mode === OENActivationExpiryMode.EXPIRY
        ) {
          validityOutcome = true;
        } else {
          this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("format"));
        }
      } else {
        this.setValidityState(ICNInputControlState.INACTIVE);
        validityOutcome = true;

        if (
          (this.data?.value == null || this.data?.input?.value == null) &&
          (this.data?.input?.placeholder === "NOW" || this.data?.input?.placeholder === "GTC")
        ) {
          this.data.calendarLocked = this.data.inputTimeFieldLocked = true;
          this.data.calendarVisible = this.data.inputTimeFieldVisible = false;
        }
      }

      if (validityOutcome && this.data.state === ICNInputControlState.INVALID) {
        this.setValidityState(ICNInputControlState.VALID);
      }
    }

    this.cd.markForCheck();

    return validityOutcome;
  }

  private isValidCustom(inputValid: boolean) {
    return (
      inputValid &&
      this.data.marketValue === "CUSTOM" &&
      this.data.dateValue &&
      this.data.timeValue &&
      this.data.timezoneValue
    );
  }

  private validateFormat(dateTime: string, tz: string): boolean {
    try {
      let dateTimeValid: boolean = this._oeTimeMachine.isDateTimeValidMoment(dateTime);
      let timezoneValid: boolean = this._oeTimeMachine.isTimeZoneNameValid(tz);
      return dateTimeValid && timezoneValid;
    } catch (ex) {
      return false;
    }
  }

  private validateMoment(dateTime: string, tz: string): boolean {
    try {
      let dateTimeValid: boolean = this._oeTimeMachine.isDateTimeValidMoment(dateTime);
      let timezoneValid: boolean = this._oeTimeMachine.isTimeZoneNameValid(tz);
      if (dateTimeValid && timezoneValid) {
        let currentMoment = this._oeTimeMachine.createNowMoment4Timezone(tz);
        let controlMoment = this._oeTimeMachine.createMoment4String(dateTime + " " + tz);
        if (
          currentMoment != null &&
          currentMoment instanceof CubiqTS.moment &&
          currentMoment.isValid() &&
          controlMoment != null &&
          controlMoment instanceof CubiqTS.moment &&
          controlMoment.isValid()
        ) {
          let difference = currentMoment.diff(controlMoment, "minutes");
          if (difference <= this.config.minutesBeforeAllowed) {
            let result = this.validateConstraint(controlMoment, true, true);
            if (result && this.config.updateToCurrentOnValidate && difference > 0) {
              // overwrite for resetting to now
              let inputArr: Array<string> = (
                currentMoment.format(
                  "DD-MMM-YY " + this.showSeconds ? SECS_TIME_FORMAT : NOSECS_TIME_FORMAT,
                ) +
                " " +
                tz
              ).split(" ");
              this.formatManualInputAndAllocate(inputArr);
            }
            return result;
          } else {
            this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("future"));
          }
        } else {
          this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("format"));
        }
      } else {
        dateTimeValid
          ? this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("zone"))
          : this.setValidityState(ICNInputControlState.INVALID, this.msgTemplate.get("format"));
      }
    } catch (ex) {
      this.setValidityState(ICNInputControlState.INVALID);
      return false;
    }
    return false;
  }

  private validateConstraint(
    controlMoment: CubiqTS.Moment,
    skipValidate: boolean = false,
    dataValueAsValid: boolean = false,
  ): boolean {
    if (
      !(controlMoment != null && CubiqTS.moment.isMoment(controlMoment) && controlMoment.isValid())
    ) {
      return false;
    } else if (
      this.input_constraint != null &&
      this.input_constraint instanceof ICGoodFromUntilModel &&
      (this.data?.value?.valid || dataValueAsValid) &&
      controlMoment != null &&
      CubiqTS.moment.isMoment(controlMoment) &&
      controlMoment.isValid()
    ) {
      let constraintMoment = this._oeTimeMachine.createMoment4Model(this.input_constraint);
      if (
        constraintMoment != null &&
        constraintMoment instanceof CubiqTS.moment &&
        constraintMoment.isValid()
      ) {
        if (this.mode === OENActivationExpiryMode.EXPIRY) {
          // goodUntil - true
          if (controlMoment.isAfter(constraintMoment)) {
            // constraint is good from
            this.setValidityState(ICNInputControlState.VALID);
            return true;
          } else {
            this.setValidityState(
              ICNInputControlState.INVALID,
              this.msgTemplate.get("constraint_until"),
            );
            return false;
          }
        } else {
          // goodFrom - false
          if (controlMoment.isBefore(constraintMoment)) {
            // constraint is good until
            this.setValidityState(ICNInputControlState.VALID);
            return true;
          } else {
            this.setValidityState(
              ICNInputControlState.INVALID,
              this.msgTemplate.get("constraint_from"),
            );
            return false;
          }
        }
      } else if (!skipValidate) {
        return this.validate();
      } else {
        this.setValidityState(ICNInputControlState.VALID);
        return true;
      }
    } else if (
      this.input_constraint == null &&
      this.defaultDayConstant != null &&
      this.mode === OENActivationExpiryMode.EXPIRY &&
      this.data.state !== ICNInputControlState.INVALID &&
      this.data.marketValue === "DAY"
    ) {
      // ignore NOW in input_constraint ( )
      this.setValidityState(ICNInputControlState.INACTIVE);
      return true;
    } else if (!this.input_constraintValid && this.data.state !== ICNInputControlState.INVALID) {
      // other field isnt valid
      if (this.mode === OENActivationExpiryMode.EXPIRY) {
        // goodUntil - true
        this.setValidityState(
          ICNInputControlState.INVALID,
          this.msgTemplate.get("constraint_until"),
        );
        return false;
      } else {
        this.setValidityState(
          ICNInputControlState.INVALID,
          this.msgTemplate.get("constraint_from"),
        );
        return false;
      }
    } else if (this.input_constraintValid) {
      // set valid when input constraint no longer invalid
      this.setValidityState(ICNInputControlState.VALID);
      if (!skipValidate) {
        return this.validate();
      } else {
        return true;
      }
    } else if (this.data.state === ICNInputControlState.INVALID) {
      // preserve existing validation message.
      return false;
    }
    this.setValidityState(ICNInputControlState.VALID);
    return true;
  }

  private formatManualInputAndAllocate(inputArr: Array<string>): void {
    this.data.input.value = "CUSTOM";
    this.autocompleteControl.writeValue("CUSTOM");
    this.data.marketValue = "CUSTOM";

    if (inputArr.length === 3) {
      this.allocateManualInput(inputArr[0], inputArr[1], inputArr[2]);
    } else {
      logger.out.error(
        `ERROR: GoodFromUntil.allocateManualInput(): Invalid array size  ${inputArr.length}`,
      );
    }
  }

  private allocateManualInput(
    date: string,
    time: string,
    tz: string,
    marketValue: string = "CUSTOM",
  ): void {
    this.data.timeValue = time;
    this.data.dateValue = date;
    this.data.timezoneValue = tz;
    this.data.marketValue = marketValue;
    let calendarData = this.calendarControl.data;
    let inputTimeControl = this.inputTimeField.data;
    /////
    calendarData.timeValue = this.data.timeValue;
    calendarData.timezoneValue = this.data.timezoneValue;
    calendarData.dateValue = this.data.dateValue;
    calendarData.marketValue = this.data.marketValue;
    inputTimeControl.timeValue = this.data.timeValue;
    inputTimeControl.timezoneValue = new ICAutocompleteGenericModel(
      this.data.timezoneValue,
      this.data.timezoneValue,
    );
    inputTimeControl.timeInputModel = this.data.timeValue;
    this.cd.markForCheck();
  }

  private setValidityState(state: ICNInputControlState, msg: string = null): void {
    if (
      this.autocompleteControl == null ||
      this.inputTimeField == null ||
      this.calendarControl == null
    ) {
      this.pendingState = { state: state, msg: msg };
      return;
    } else {
      this.pendingState = null;
    }

    if (
      state === ICNInputControlState.VALID ||
      state === ICNInputControlState.INACTIVE ||
      state === ICNInputControlState.ACTIVE
    ) {
      this.data.state = state;
      this.data.errorMsg = null;
      this.data.showError = false;
      if (this.data?.value != null) {
        this.data.value.valid = true;
      }
      this.input_externalState = null;
      this.errorMsg = null;
      switch (state) {
        case ICNInputControlState.VALID:
          this.autocompleteControl.state = BfxAutoCompleteComponentState.VALID;
          this.inputTimeField.state = BfxInputControlStateEnum.VALID;
          this.calendarControl.state = BfxInputControlStateEnum.VALID;
          break;
        case ICNInputControlState.INACTIVE:
          this.autocompleteControl.state = BfxAutoCompleteComponentState.INACTIVE;
          this.inputTimeField.state = BfxInputControlStateEnum.INACTIVE;
          this.calendarControl.state = BfxInputControlStateEnum.INACTIVE;
          break;
        case ICNInputControlState.ACTIVE:
          break;
        default:
          break;
      }
    } else if (state === ICNInputControlState.INVALID) {
      this.data.state = ICNInputControlState.INVALID;
      if (this.data?.value != null) {
        this.data.value.valid = false;
      }
      if (
        (msg != null && msg !== "") ||
        (this.data.errorMsg != null && this.data.errorMsg !== "")
      ) {
        this.data.errorMsg = msg != null && msg !== "" ? msg : this.data.errorMsg;
        this.errorMsg = this.data.errorMsg;
        this.data.showError = true;
      } else {
        this.errorMsg = null;
        this.data.errorMsg = null;
        this.data.showError = false;
      }
      this.autocompleteControl.state = BfxAutoCompleteComponentState.INVALID;
      this.calendarControl.state = BfxAutoCompleteComponentState.INVALID;
      this.inputTimeField.state = BfxInputControlStateEnum.INVALID;
    }

    this.cd.markForCheck();
  }

  protected searchMarketTimeConstantNames(
    query: string,
    includeGTCandDAY: boolean = false,
  ): Array<string> {
    let result: Array<string> = [];

    let found: Array<string> = [];
    if (includeGTCandDAY) {
      found.push("CUSTOM");
      found.push("GTC");
      found.push("DAY");
    } else {
      found.push("CUSTOM");
      found.push("NOW");
    }
    found = found.concat(this.data.marketNames);

    if (query != null && query.length > 0) {
      result = found.filter(
        (item) => item != null && item.toLowerCase().indexOf(query.toLowerCase()) > -1,
      );
    } else {
      result = found;
    }

    result.sort((a, b) => a.localeCompare(b));

    return result;
  }

  protected formatMarketTimeConstantName(mtc: string): string {
    let dataset = this.data.marketNames;
    let result = null;
    if (dataset != null && dataset.length > 0) {
      result = dataset.find((item) => item.toLowerCase() === mtc.toLowerCase());
    }

    return result ?? mtc;
  }

  protected getMarketTimeConstantImpl(mtc: string): CubiqTS.MarketTimeConstantImpl {
    return this.data.marketList.find(
      (marketConstant: CubiqTS.MarketTimeConstantImpl) =>
        mtc != null &&
        typeof mtc === "string" &&
        marketConstant != null &&
        typeof marketConstant.constantName === "string" &&
        mtc.toLowerCase() === marketConstant.constantName.toLowerCase(),
    );
  }

  protected createMoment4MarketConstant(marketTimeConstantName: string): CubiqTS.Moment | null {
    try {
      let dataset: CubiqTS.MarketTimeConstantImpl[] = this.data.marketList;

      if (this.defaultDayConstant != null) {
        return CubiqTS.moment(this.defaultDayConstant);
      }

      if (
        marketTimeConstantName != null &&
        marketTimeConstantName &&
        dataset != null &&
        dataset.length > 0
      ) {
        let marketTimeConstantImpl: CubiqTS.MarketTimeConstantImpl =
          this.getMarketTimeConstantImpl(marketTimeConstantName);
        if (
          marketTimeConstantImpl != null &&
          marketTimeConstantImpl instanceof CubiqTS.MarketTimeConstantImpl
        ) {
          return this._oeTimeMachine.createMomentForMarketTimeConstantImpl(marketTimeConstantImpl);
        } else {
          throw new Error(`marketTimeConstant ${marketTimeConstantName} not found!`);
        }
      } else {
        throw new Error(
          `Invalid marketTimeConstant name: ${marketTimeConstantName}, or dataset missing (size: ${dataset.length})!`,
        );
      }
    } catch (ex) {
      logger.out.error(`ERROR: ${this.constructor.name}.createMoment4MarketConstant(): `, ex);
      return null;
    }
  }

  protected isDayConstantLoaded(): boolean {
    let loaded: boolean = false;

    if (
      this.data.marketList != null &&
      Array.isArray(this.data.marketList) &&
      this.data.marketList.length > 0
    ) {
      let marketConstDay: CubiqTS.MarketTimeConstantImpl = this.data.marketList.find(
        (marketConst: CubiqTS.MarketTimeConstantImpl) => marketConst.constantName === "DAY",
      );
      loaded = marketConstDay?.constantTime != null;
    }

    if (this.defaultDayConstant) {
      loaded = true;
    }

    return loaded;
  }

  public calendarAssignToModel(data: Record<string, any>): void {
    if (
      this.data.value != null &&
      this.data.value instanceof ICGoodFromUntilModel &&
      this.data.value.isCustom
    ) {
      this.data.dateValue = data.dateValue;
      this.data.value.dateValue = data.dateValue;
      this.data.value.marketValue = "CUSTOM";
      this.data.value.isNow = false;
      this.onChange(this.data.value);
      this.calendarControl.updateInputField();
    } else if (this.data.value != null && this.data.value.dateValue !== data.dateValue) {
      this.data.dateValue = data.dateValue;
      this.data.marketValue = "CUSTOM";
      this.data.dateValue = data.dateValue;
      this.data.value.dateValue = data.dateValue;
      this.data.value.isNow = false;
      this.data.value.isCustom = true;
      this.data.value.dayOrder = false;
      this.data.value.marketValue = data.marketValue;
      this.data.value = Object.assign(new ICGoodFromUntilModel(), this.data.value);
      this.data.input.value = "CUSTOM";
      this.onChange(this.data.value);
      this.autocompleteControl.writeValue("CUSTOM");
      this.calendarControl.updateInputField();
    } else {
      // ignore existing model representation from constants on closing (keep from setting custom)
      this.focusTimeFieldInput();
      return;
    }
    this.cd.markForCheck();
    this.validate();
    this.focusTimeFieldInput();
  }

  private timeValueUnchanged(value): boolean {
    if (this.data.value?.timeValue === value) {
      return true;
    }

    if (
      typeof this.data.value?.timeValue === "string" &&
      typeof value === "string" &&
      value != null &&
      this.data.value?.timeValue === value.replace(":", "")
    ) {
      return true;
    }

    return false;
  }

  timeAssignToModel(data: Record<string, any>) {
    if (
      this.data.value != null &&
      typeof data?.timeValue == "string" &&
      data?.timeValue != "HH:MM"
    ) {
      const isTimeControlModel = this.data?.value?.timeValue instanceof ICTimeControlModel;
      const isCustom = !(
        (isTimeControlModel
          ? this.data.value.timeValue?.timeValue === data.timeValue
          : this.timeValueUnchanged(data.timeValue)) &&
        this.data.value?.timezoneValue === data?.timezoneValue?.value
      );

      this.data.value.timeValue = data.timeValue;
      this.data.value.timezoneValue = data?.timezoneValue?.value ?? null;
      this.data.timezoneValue = data?.timezoneValue?.value ?? null;

      if (isCustom) {
        this.data.value.isCustom = true;
        this.data.value.dayOrder = false;
        this.data.value.marketValue = "CUSTOM";
        this.data.marketValue = "CUSTOM";
        this.data.value = Object.assign(new ICGoodFromUntilModel(), this.data.value);
        this.data.input.value = "CUSTOM";
        this.autocompleteControl.writeValue("CUSTOM");
      }

      this.validate();

      this.onChange(this.data.value);
    }
  }

  public focusTimeFieldInput() {
    this.autocompleteControl.fieldState = "enabled";
    this.inputTimeField.focusInput();
  }

  onTimeInputBlur($event: any) {
    this.calendarControl.onTimeInputBlur($event);
    this.inputFocused = false;
    this.validate();
  }

  onTimeInputFocus($event: any) {
    this.calendarControl.onTimeInputBlur($event);
    this.inputFocused = false;
  }

  onCalendarBlur(_) {
    if (!this.calendarControl.overlayShown) {
      this.focusTimeFieldInput();
    }
  }

  onCalendarFocus(_) {
    this.inputFocused = false;
  }
}
